import { Collapse, Descriptions, Empty, Table, Tag, Typography } from "antd";
import { DateField, NumberField } from "@refinedev/antd";
import { OtherServicesTable } from "./OtherServicesTable";
import { formatEnrollmentDate } from "components/metadata/enrollmentDate";

const { Text } = Typography;

type SnapshotValue =
  | string | number | boolean | null | undefined
  | SnapshotValue[]
  | { [k: string]: SnapshotValue };

interface Props {
  data: SnapshotValue;
  depth?: number;
  keyName?: string;
}

const ACRONYMS = new Set(["ukho", "imo", "enp", "adp", "avcs", "arcs", "id", "url"]);
// Fixed label column so nested sections (AVCS/ADP/ENP/Others) align instead of
// auto-sizing to their own content.
const NESTED_LABEL_STYLE = { label: { width: 240 } };
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T/;
const TEMPORAL_KEY = /(At|Date)$|expires|starts|ends/i;
// Enrollment dates are UTC-anchored calendar dates (see components/metadata/enrollmentDate),
// not instants. Rendering them through <DateField> (local timezone) turns the 1950-01-01
// legacy sentinel into 31-Dec-1949 for any viewer west of UTC. createdAt and other temporal
// keys are genuine instants and must keep rendering in local time via <DateField> below.
const ENROLLMENT_DATE_KEY = /^enrollmentDate$|EnrollmentDate$/;
const MAX_DEPTH = 4;

const humanize = (key: string): string =>
  key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .filter(Boolean)
    .map((w) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");

const looksLikeDate = (key: string, value: string) =>
  ISO_DATE.test(value) && TEMPORAL_KEY.test(key);

const isPlainObject = (v: any): v is Record<string, SnapshotValue> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

// Known top-level shape written by emr-api lookupSubscriptionMetadata:
// { ukhoLicenceId, avcs, adp, enp, others, othersCreatedAt }
const SNAPSHOT_KEY_ORDER = ["ukhoLicenceId", "avcs", "adp", "enp"];

const isTopLevelSnapshot = (data: Record<string, SnapshotValue>): boolean =>
  "others" in data || SNAPSHOT_KEY_ORDER.some((k) => k in data);

const isHomogeneousObjectArray = (arr: any[]): boolean => {
  if (arr.length === 0) return false;
  if (!arr.every(isPlainObject)) return false;
  const keys = Object.keys(arr[0]).sort().join(",");
  return arr.every((item) => Object.keys(item).sort().join(",") === keys);
};

const renderPrimitive = (value: SnapshotValue, keyName?: string) => {
  if (value === null || value === undefined || value === "") {
    return <Text type="secondary" italic>—</Text>;
  }
  if (typeof value === "boolean") {
    return <Tag color={value ? "green" : "default"}>{String(value)}</Tag>;
  }
  if (typeof value === "number") {
    return <NumberField value={value} />;
  }
  if (typeof value === "string") {
    if (keyName && ENROLLMENT_DATE_KEY.test(keyName)) {
      return <Text>{formatEnrollmentDate(value)}</Text>;
    }
    if (keyName && looksLikeDate(keyName, value)) {
      return <DateField value={value} format="DD-MMM-YYYY HH:mm" />;
    }
    return <Text>{value}</Text>;
  }
  return <Text type="secondary">{String(value)}</Text>;
};

export const SubscriptionSnapshotView: React.FC<Props> = ({ data, depth = 0, keyName }) => {
  if (data === null || data === undefined) {
    return <Empty description="No subscription snapshot available" />;
  }

  if (isPlainObject(data) && Object.keys(data).length === 0) {
    return <Empty description="No subscription snapshot available" />;
  }

  if (depth >= MAX_DEPTH) {
    return (
      <Collapse size="small" items={[{
        key: "raw",
        label: "Show raw",
        children: <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(data, null, 2)}</pre>,
      }]} />
    );
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <Text type="secondary" italic>—</Text>;
    }
    const allPrimitive = data.every((v) => v === null || typeof v !== "object");
    if (allPrimitive) {
      return (
        <>
          {data.map((v, i) => (
            <Tag key={i} style={{ marginBottom: 4 }}>{String(v ?? "—")}</Tag>
          ))}
        </>
      );
    }
    if (isHomogeneousObjectArray(data)) {
      const cols = Object.keys(data[0] as object).map((k) => ({
        title: humanize(k),
        dataIndex: k,
        key: k,
        render: (v: SnapshotValue) => (
          isPlainObject(v) || Array.isArray(v)
            ? <SubscriptionSnapshotView data={v} depth={depth + 1} keyName={k} />
            : renderPrimitive(v, k)
        ),
      }));
      return (
        <Table
          size="small"
          rowKey={(_r, i) => String(i)}
          pagination={false}
          columns={cols}
          dataSource={data as any[]}
        />
      );
    }
    return (
      <ul style={{ marginLeft: 16, paddingLeft: 0 }}>
        {data.map((v, i) => (
          <li key={i} style={{ marginBottom: 4 }}>
            <SubscriptionSnapshotView data={v} depth={depth + 1} keyName={keyName} />
          </li>
        ))}
      </ul>
    );
  }

  if (isPlainObject(data) && depth === 0 && isTopLevelSnapshot(data)) {
    const { others, othersCreatedAt, ...rest } = data;
    const ordered = [
      ...SNAPSHOT_KEY_ORDER.filter((k) => k in rest),
      ...Object.keys(rest).filter((k) => !SNAPSHOT_KEY_ORDER.includes(k)),
    ];
    return (
      <Descriptions size="small" column={1} bordered>
        {ordered.map((k) => (
          <Descriptions.Item key={k} label={humanize(k)}>
            {isPlainObject(rest[k]) || Array.isArray(rest[k])
              ? <SubscriptionSnapshotView data={rest[k]} depth={1} keyName={k} />
              : renderPrimitive(rest[k], k)}
          </Descriptions.Item>
        ))}
        <Descriptions.Item key="others" label="Other Services">
          <OtherServicesTable
            others={isPlainObject(others) ? others : null}
            createdAt={othersCreatedAt}
          />
        </Descriptions.Item>
      </Descriptions>
    );
  }

  if (isPlainObject(data)) {
    return (
      <div style={{ marginLeft: depth === 0 ? 0 : 12 }}>
        <Descriptions size="small" column={1} bordered styles={NESTED_LABEL_STYLE}>
          {Object.entries(data).map(([k, v]) => (
            <Descriptions.Item key={k} label={humanize(k)}>
              {isPlainObject(v) || Array.isArray(v)
                ? <SubscriptionSnapshotView data={v} depth={depth + 1} keyName={k} />
                : renderPrimitive(v, k)}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </div>
    );
  }

  return <>{renderPrimitive(data, keyName)}</>;
};
